import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { login, getProfile } from '../../api/auth';
import { getBranding } from '../../api/branding';
import { getMyPermissions } from '../../api/rbac';
import { useAuthStore } from '../../stores/authStore';
import { useBrandingStore } from '../../stores/brandingStore';
import { ROUTES } from '../../constants/routes';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '../../components/ui/form';

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type FormValues = z.infer<typeof schema>;

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  if (token && user) {
    if (isSuperAdmin()) return <Navigate to={ROUTES.PLATFORM} replace />;
    if (user.must_change_password) return <Navigate to={ROUTES.FIRST_LOGIN_CHANGE_PASSWORD} replace />;
    if (useAuthStore.getState().isIcBroker()) return <Navigate to={ROUTES.BROKER_DASHBOARD} replace />;
    if (useAuthStore.getState().isIcAdmin() && !user.onboarding_completed) return <Navigate to={ROUTES.ONBOARDING} replace />;
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  const onSubmit = async (values: FormValues) => {
    try {
      const response = await login(values.email, values.password);

      if (response.status === 'mfa_required') {
        navigate(ROUTES.MFA, { state: { fromLogin: true } });
        return;
      }

      // success
      const fullToken = response.data.authorization.token;
      const mustChangePw = response.data.must_change_password;
      useAuthStore.setState({ token: fullToken });

      const [profile, branding] = await Promise.all([getProfile(), getBranding()]);

      useAuthStore.getState().setAuth(profile.user, fullToken, profile.company);
      useBrandingStore.getState().setBranding(branding);

      // Load effective permissions (Layer 1+2+3) — fire-and-forget; non-blocking
      getMyPermissions()
        .then((perms) => useAuthStore.getState().setEffectivePermissions(perms.effective as string[] | '*'))
        .catch(() => {/* silently ignored — CAPABILITY_MAP fallback will be used */});

      const superAdmin = useAuthStore.getState().isSuperAdmin();
      if (superAdmin) {
        navigate(ROUTES.PLATFORM, { replace: true });
      } else if (mustChangePw || profile.user.must_change_password) {
        navigate(ROUTES.FIRST_LOGIN_CHANGE_PASSWORD, { replace: true });
      } else if (useAuthStore.getState().isIcBroker()) {
        navigate(ROUTES.BROKER_DASHBOARD, { replace: true });
      } else if (useAuthStore.getState().isIcAdmin() && !profile.user.onboarding_completed) {
        navigate(ROUTES.ONBOARDING, { replace: true });
      } else {
        const from = (location.state as { from?: { pathname: string } })?.from?.pathname;
        navigate(from ?? ROUTES.DASHBOARD, { replace: true });
      }
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message ?? 'Login failed. Please try again.';
      form.setError('root', { message });
    }
  };

  return (
    <Card className="w-full max-w-md shadow-lg">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl text-center">Sign in</CardTitle>
        <CardDescription className="text-center">
          Enter your credentials to access your account
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="you@company.com"
                      autoComplete="email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        autoComplete="current-password"
                        className="pr-10"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.formState.errors.root && (
              <p className="text-sm font-medium text-destructive">
                {form.formState.errors.root.message}
              </p>
            )}

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate(ROUTES.FORGOT_PASSWORD)}
                className="text-sm text-primary hover:underline"
              >
                Forgot password?
              </button>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in…
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
